import React, { useState } from 'react';
import classNames from 'classnames';
import { EnvironmentOutlined, EllipsisOutlined } from '@ant-design/icons';
import CcmHospitalIcon from 'components/atoms/icons/ccm-hospital';
import BaseCheckbox from 'components/atoms/base-checkbox';
import BaseTag from 'components/atoms/base-tag';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'antd';

import s from './styles.module.scss';

import type { MenuProps } from 'antd';

interface IProps {
  name: string;
  isParent: boolean;
  isLast: boolean;
  checked: boolean;
  withoutParent?: boolean;
  showIcon?: boolean;
  childrenCount?: number;
  showCheckBox?: boolean;
  onChange: (checked: boolean) => void;
  parentOnChange: (key: string) => void;
}

const ClinicFilterFormListItem: React.FC<IProps> = ({
  name,
  isParent,
  isLast,
  checked,
  showIcon = true,
  showCheckBox = true,
  withoutParent = false,
  childrenCount,
  onChange,
  parentOnChange,
}) => {
  const [openDropdown, setOpenDropdown] = useState<boolean>(false);
  const { t } = useTranslation();

  const icon = isParent ? <CcmHospitalIcon /> : <EnvironmentOutlined />;

  const itemClassNames = classNames(s.item, {
    [s.child]: !isParent && !withoutParent,
    [s.parent]: isParent && !isLast,
    [s.open]: openDropdown,
    [s.checked]: checked,
  });

  const onCheck = () => {
    if (showCheckBox && !openDropdown) onChange(!checked);
  };

  const parentOnCheck = (value: any) => {
    parentOnChange(value.key);
  };

  const parentClinicOptions = [
    {
      label: t('labels.selectAll'),
      id: 'all',
    },
    {
      label: t('labels.selectOnlyParent'),
      id: 'parent',
    },
    {
      label: t('labels.selectOnlyChildren'),
      id: 'children',
    },
    {
      label: t('labels.clearAll'),
      id: 'none',
    },
  ];

  const items: MenuProps['items'] = parentClinicOptions.map((option) => ({
    label: option.label,
    key: option.id,
    onClick: (value) => parentOnCheck(value),
  }));

  const isParentClinicWithChildren = isParent && !isLast;

  return (
    <li className={itemClassNames} onClick={onCheck} data-cy="clinicList-item">
      <div className={s.title} data-cy="clinicList-title">
        {showIcon && <i>{icon}</i>}
        {name}
        {isParentClinicWithChildren && (
          <>
            <BaseTag label={t('labels.parent')} />
            <span className={s['children-count']}>
              {childrenCount} {t('labels.location', { count: childrenCount })}
            </span>
          </>
        )}
      </div>

      {(showCheckBox || isParentClinicWithChildren) && (
        <div className={s.checkbox} data-cy="clinicList-checkbox">
          {isParentClinicWithChildren && (
            <Dropdown
              menu={{ items }}
              trigger={['click']}
              rootClassName={s.menu}
              getPopupContainer={(trigger) => trigger}
              onOpenChange={() => setOpenDropdown((prevState) => !prevState)}
              placement="bottomRight"
            >
              <div
                className={s['caret-wrapper']}
                onClick={(e) => e.stopPropagation()}
              >
                <EllipsisOutlined />
              </div>
            </Dropdown>
          )}
          {showCheckBox && <BaseCheckbox value={checked} />}
        </div>
      )}
    </li>
  );
};

export default ClinicFilterFormListItem;
